import { siteInfo } from '@/mocks/siteInfo';
import { useLocation } from 'react-router-dom';

interface SchemaOrgProps {
  pageType?: 'home' | 'category' | 'article' | 'about';
  categoryName?: string;
  articleTitle?: string;
  articleDate?: string;
  articleAuthor?: string;
  articleImage?: string;
  faqs?: { question: string; answer: string }[];
}

export default function SchemaOrg({
  pageType = 'home',
  categoryName,
  articleTitle,
  articleDate,
  articleAuthor,
  articleImage,
  faqs,
}: SchemaOrgProps) {
  const location = useLocation();
  const currentUrl = `${siteInfo.url}${location.pathname}`;
  const breadcrumbItems = location.pathname.split('/').filter(Boolean);

  const breadcrumbList = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: siteInfo.url,
      },
      ...breadcrumbItems.map((item, index) => {
        const decodedItem = decodeURIComponent(item);
        let displayName = decodedItem;
        if (decodedItem === 'category') {
          displayName = categoryName || '分类';
        }
        if (decodedItem === 'article') {
          displayName = articleTitle || '文章';
        }
        return {
          '@type': 'ListItem' as const,
          position: index + 2,
          name: displayName,
          item: `${siteInfo.url}/${breadcrumbItems.slice(0, index + 1).join('/')}`,
        };
      }),
    ],
  };

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteInfo.name,
    url: siteInfo.url,
    description: siteInfo.description,
    email: siteInfo.email,
    telephone: siteInfo.phone,
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'CN',
      addressLocality: '北京',
      streetAddress: siteInfo.address,
    },
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: siteInfo.name,
    url: siteInfo.url,
    description: siteInfo.description,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${siteInfo.url}/search?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: pageType === 'home' ? siteInfo.name : document.title,
    url: currentUrl,
    description: siteInfo.description,
    isPartOf: {
      '@type': 'WebSite',
      name: siteInfo.name,
      url: siteInfo.url,
    },
  };

  const schemas = [organizationSchema, websiteSchema, webpageSchema, breadcrumbList];

  if (pageType === 'article' && articleTitle) {
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: articleTitle,
      author: {
        '@type': 'Organization',
        name: articleAuthor || siteInfo.name,
      },
      datePublished: articleDate,
      image: articleImage,
      publisher: {
        '@type': 'Organization',
        name: siteInfo.name,
        url: siteInfo.url,
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': currentUrl,
      },
    });
  }

  if (faqs && faqs.length > 0) {
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    });
  }

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(
          schemas.length === 1 ? schemas[0] : schemas,
          null,
          0
        ),
      }}
    />
  );
}